import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { SelectDropdown } from "../../components/SelectDropdown";
import {
    catalogViews,
    PRODUCT_CATALOG_EXPORT_EXCLUDED_COLUMN_IDS,
    productCatalogProducts,
    productCatalogStatuses,
    normalizeProductCatalogCustomViews,
} from "../../consts/productCatalog";
import { ArrowDownTrayIcon, ArrowPathIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, Cog8ToothIcon, MagnifyingGlassIcon, PlusIcon, RectangleStackIcon, Squares2X2Icon, StarIcon as StarIconOutline, ViewColumnsIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { ProductCatalogColumnsSettings } from "../../components/productCatalog/ProductCatalogColumnsSettings";
import { ProductCatalogCatalogHubDrawer } from "../../components/productCatalog/ProductCatalogCatalogHubDrawer";
import { ProductCatalogTable } from "../../components/productCatalog/ProductCatalogTable";
import { ProductCatalogProductSidebar } from "../../components/productCatalog/ProductCatalogProductSidebar";
import { ProductCatalogHistoryDrawer } from "../../components/productCatalog/ProductCatalogHistoryDrawer";
import { ProductCatalogAddProductDrawer } from "../../components/productCatalog/ProductCatalogAddProductDrawer";
import {
    DEFAULT_COLUMNS,
    cloneColumns,
    normalizeCatalogColumns,
    normalizeCatalogColumnsByView,
} from "../../consts/productCatalog";
import { ROUTES } from "../../consts/general";
import {
    fetchProductCatalogColumnsMetadata,
    fetchProductCatalogCategorySettings,
    fetchProductCatalogPresetSettings,
    fetchProductCatalogContainerSettings,
    fetchProductCatalogDefaultsSettings,
    fetchTierDiscountDrivers,
    fetchProductCatalogTable,
    ProductCatalogLoadingKeys,
    restoreDeletedCatalogProduct,
    saveProductCatalogDefaultsSettings,
    saveTierDiscountDrivers,
    saveProductCatalogColumnsMetadata,
    updateProductCatalogProductSettings,
} from "../../slices/productCatalogSlice";
import { getSuppliers, ProductGroupLoadingKeys } from "../../slices/productGroupSlice";
import { updateVendorByProduct } from "../../slices/productsSlice";
import { ProductCatalogSelectFilter } from "../../components/productCatalog/ProductCatalogSelectFilter";
import { ProductCatalogPageSizeSelect } from "../../components/productCatalog/ProductCatalogPageSizeSelect";
import { arrayHasItems } from "../../helpers/common";
import { useDebounce } from "../../helpers/useDebounce";
import { usePageReset } from "../../helpers/usePageReset";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import { ConfrimModal } from "../../components/ConfrimModal";
import { ProductCatalogSaveViewModal } from "../../components/productCatalog/ProductCatalogSaveViewModal";
import { deleteProduct, ProductCostsLoadingKeys } from "../../slices/productCostSlice";

const PRODUCT_CATALOG_TABLE_URL = "/api/v1/product-catalog";
const EXPORT_CSV_MAX_ROWS = 100000;

function escapeCsvField(value) {
    if (value === null || value === undefined) return "";
    const s = String(value);
    if (/[",\r\n]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

/** Merge pending catalog cell payloads per product (multiple columns → one API patch). */
function mergeCatalogPendingPayload(prev, next) {
    if (!prev) return { ...next };
    return { ...prev, ...next };
}

const mergeColumnsWithDefaults = (savedColumns = []) => {
    const savedColumnsMap = new Map(
        normalizeCatalogColumns(savedColumns).map((column) => [column.id, column])
    );
    return DEFAULT_COLUMNS.map((defaultColumn) => {
        const saved = savedColumnsMap.get(defaultColumn.id);
        return {
            ...defaultColumn,
            ...(saved || {}),
            // Width always follows `DEFAULT_COLUMNS` so updates in consts apply; saved metadata
            // otherwise keeps stale widths (e.g. Variant stuck at 130px after default became 250px).
            width: defaultColumn.width,
            label: defaultColumn.label,
            readonly: defaultColumn.readonly === true,
            pinned: ["fba_asin", "fbm_asin"].includes(defaultColumn.id)
                ? defaultColumn.pinned
                : (saved?.pinned ?? defaultColumn.pinned),
        };
    });
};

/** Keeps `selectedCatalogItem` aligned with `updateProductCatalogProductSettings` response (denormalized labels, JSON channel ids). */
const mergeSelectedCatalogItemWithSettingsResponse = (prev, payload, updated) => {
    if (!prev) return null;
    const cubic =
        updated?.length != null &&
        updated?.width != null &&
        updated?.height != null
            ? Number((Number(updated.length) * Number(updated.width) * Number(updated.height)).toFixed(2))
            : prev.cubic_inches;
    return {
        ...prev,
        ...payload,
        msrp: updated?.msrp ?? prev.msrp,
        msrp_from_shopify: updated?.msrp_from_shopify ?? prev.msrp_from_shopify,
        msrp_editable: updated?.msrp_editable ?? prev.msrp_editable,
        manual_msrp: updated?.manual_msrp ?? prev.manual_msrp,
        sell_on_shopify: updated?.sell_on_shopify ?? prev.sell_on_shopify,
        sell_on_amazon: updated?.sell_on_amazon ?? prev.sell_on_amazon,
        fbm_channel_product_id: updated?.fbm_channel_product_id ?? prev.fbm_channel_product_id,
        fba_channel_product_id: updated?.fba_channel_product_id ?? prev.fba_channel_product_id,
        packaging_product_id: updated?.packaging_product_id ?? prev.packaging_product_id,
        amazon_fbm_sku: updated?.amazon_fbm_sku ?? prev.amazon_fbm_sku,
        amazon_fba_sku: updated?.amazon_fba_sku ?? prev.amazon_fba_sku,
        packaging_sku: updated?.packaging_sku ?? prev.packaging_sku,
        lead_time_days: updated?.lead_time_days ?? prev.lead_time_days,
        transit_lead_time_days: updated?.transit_lead_time_days ?? prev.transit_lead_time_days,
        total_lead_time_days: updated?.total_lead_time_days ?? prev.total_lead_time_days,
        moq: updated?.moq ?? prev.moq,
        container_group_id: updated?.container_group_id ?? prev.container_group_id,
        container_group: updated?.container_group ?? prev.container_group,
        container_qty: updated?.container_qty ?? prev.container_qty,
        carton_qty: updated?.carton_qty ?? prev.carton_qty,
        launch_date: updated?.launch_date ?? prev.launch_date,
        sunset_date: updated?.sunset_date ?? prev.sunset_date,
        inactive_date: updated?.inactive_date ?? prev.inactive_date,
        color: updated?.color ?? prev.color,
        material: updated?.material ?? prev.material,
        material_primary_row_id:
            updated?.material_primary_row_id !== undefined
                ? updated.material_primary_row_id
                : prev.material_primary_row_id,
        brand_pillar: updated?.brand_pillar ?? prev.brand_pillar,
        brand_pillar_id: updated?.brand_pillar_id ?? prev.brand_pillar_id,
        demand_tier: updated?.demand_tier ?? prev.demand_tier,
        demand_tier_id: updated?.demand_tier_id ?? prev.demand_tier_id,
        intent: updated?.intent ?? prev.intent,
        intent_id: updated?.intent_id ?? prev.intent_id,
        entry_engine: updated?.entry_engine ?? prev.entry_engine,
        entry_engine_id: updated?.entry_engine_id ?? prev.entry_engine_id,
        status: updated?.status ?? prev.status,
        category: updated?.category ?? prev.category,
        subcategory: updated?.subcategory ?? prev.subcategory,
        category_group_id: updated?.category_group_id ?? prev.category_group_id,
        subcategory_variant_id: updated?.subcategory_variant_id ?? prev.subcategory_variant_id,
        is_packaging: updated?.is_packaging ?? prev.is_packaging,
        unit_weight_lbs:
            updated?.weight !== undefined && updated?.weight !== null ? updated.weight : prev.unit_weight_lbs,
        unit_length:
            updated?.length !== undefined && updated?.length !== null ? updated.length : prev.unit_length,
        unit_width:
            updated?.width !== undefined && updated?.width !== null ? updated.width : prev.unit_width,
        unit_height:
            updated?.height !== undefined && updated?.height !== null ? updated.height : prev.unit_height,
        cubic_inches: cubic ?? prev.cubic_inches,
        is_supply: updated?.is_supply ?? prev.is_supply,
        is_virtual: updated?.is_virtual ?? prev.is_virtual,
        forecastable: updated?.forecastable ?? prev.forecastable,
        needs_review: updated?.needs_review ?? prev.needs_review,
        lock_flag: updated?.lock_flag ?? prev.lock_flag,
        replacement_exit: updated?.replacement_exit ?? prev.replacement_exit,
        replacement_product_id: updated?.replacement_product_id ?? prev.replacement_product_id,
        replaces_sku: updated?.replaces_sku ?? prev.replaces_sku,
        condition_of_product_id: updated?.condition_of_product_id !== undefined
            ? updated.condition_of_product_id
            : prev.condition_of_product_id,
        condition_label: updated?.condition_label !== undefined ? updated.condition_label : prev.condition_label,
        condition_of_sku: updated?.condition_of_sku ?? prev.condition_of_sku,
    };
};

const ProductCatalog = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const pageParams = window?.location?.search ? new URLSearchParams(window?.location?.search) : null;
    const dispatch = useDispatch();

    const {
        productCatalogColumnsMetadata,
        columnsMetadataFetchStatus,
        productCatalogDefaultsSettings,
        tierDiscountDrivers,
        defaultsSettingsFetchStatus,
        tableItems,
        tableTotal,
        tablePagination,
        loadingKeys,
        isFirstLoadComplete,
        categorySettingsItems,
        categorySettingsFetchStatus,
        presetSettingsByType,
        containerSettingsItems,
    } = useSelector((s) => s.productCatalog);
    const { vendorsToSelect, loadingKeys: groupLoadingKeys } = useSelector((s) => s.productGroup);
    const { loadingKeys: productCostsLoadingKeys } = useSelector((s) => s.productCosts);

    const byViewFromStore = productCatalogColumnsMetadata.by_view;
    const customViewsFromStore = productCatalogColumnsMetadata.custom_views || [];

    const [viewTable, setViewTable] = useState(pageParams?.get("viewTable") || "default");
    const [searchTerm, setSearchTerm] = useState(pageParams?.get("search") || "");
    const [searchInput, setSearchInput] = useState(pageParams?.get("search") || "");
    const [selectedStatus, setSelectedStatus] = useState(pageParams?.get("statuses")?.split(",").filter(Boolean) || []);
    const [selectedCategories, setSelectedCategories] = useState(pageParams?.get("categories")?.split(",").filter(Boolean) || []);
    const [selectedSubcategories, setSelectedSubcategories] = useState(pageParams?.get("subcategories")?.split(",").filter(Boolean) || []);
    const [selectedBrandPillars, setSelectedBrandPillars] = useState(pageParams?.get("brandPillars")?.split(",").filter(Boolean) || []);
    const [selectedDemandTiers, setSelectedDemandTiers] = useState(pageParams?.get("tiers")?.split(",").filter(Boolean) || []);
    const [selectedEntryEngines, setSelectedEntryEngines] = useState(pageParams?.get("entryEngines")?.split(",").filter(Boolean) || []);
    const [selectedIntents, setSelectedIntents] = useState(pageParams?.get("intents")?.split(",").filter(Boolean) || []);
    const [selectedContainerGroups, setSelectedContainerGroups] = useState(pageParams?.get("containerGroups")?.split(",").filter(Boolean) || []);
    const [selectedProducts, setSelectedProducts] = useState(pageParams?.get("products")?.split(",").filter(Boolean) || []);
    const [currentPage, setCurrentPage] = useState(Number(pageParams?.get("page") || 1));
    const [currentItemsPerPage, setCurrentItemsPerPage] = useState(Number(pageParams?.get("itemsPerPage") || 50));
    const [sortBy, setSortBy] = useState(pageParams?.get("sortBy") || "");
    const [sortOrder, setSortOrder] = useState(pageParams?.get("sortOrder") === "desc" ? "desc" : "asc");
    const [columnsSettingsOpened, setColumnsSettingsOpened] = useState(false);
    const [catalogHubOpened, setCatalogHubOpened] = useState(false);
    const [columnDefs, setColumnDefs] = useState(() => cloneColumns(DEFAULT_COLUMNS));
    const [savedColumnDefsByView, setSavedColumnDefsByView] = useState({});
    const [customViewDefinitions, setCustomViewDefinitions] = useState([]);
    const [productDetailsOpened, setProductDetailsOpened] = useState(false);
    const [productHistoryOpened, setProductHistoryOpened] = useState(false);
    const [catalogHistoryOpened, setCatalogHistoryOpened] = useState(false);
    const [selectedCatalogItem, setSelectedCatalogItem] = useState(null);
    const [productIdToDelete, setProductIdToDelete] = useState("");
    const [deleteProductModalOpened, setDeleteProductModalOpened] = useState(false);
    const [productIdToRestore, setProductIdToRestore] = useState("");
    const [restoreProductModalOpened, setRestoreProductModalOpened] = useState(false);
    const [bulkProductLifecycleLoading, setBulkProductLifecycleLoading] = useState(false);
    const [addProductOpened, setAddProductOpened] = useState(false);
    const [exportingCsv, setExportingCsv] = useState(false);
    const [saveViewModalOpened, setSaveViewModalOpened] = useState(false);
    const [deleteCustomViewModalOpened, setDeleteCustomViewModalOpened] = useState(false);
    const [customViewToDelete, setCustomViewToDelete] = useState(null);
    const didHydrateFromApi = useRef(false);
    const didHydrateDefaultsFromApi = useRef(false);
    const didRunPostDefaultsRequests = useRef(false);
    const hasItemsPerPageInInitialUrl = useRef(
        Boolean(new URLSearchParams(window?.location?.search || "").get("itemsPerPage"))
    );
    const catalogToolbarRef = useRef(null);
    const catalogTableRef = useRef(null);
    /** `productId` → merged API payload from grid edits; cleared on filter/search/page refetch. */
    const [pendingCatalogEditsByProductId, setPendingCatalogEditsByProductId] = useState({});
    const [saveCatalogChangesConfirmOpen, setSaveCatalogChangesConfirmOpen] = useState(false);
    const [savingCatalogChanges, setSavingCatalogChanges] = useState(false);
    const [unsavedNavigationConfirmOpen, setUnsavedNavigationConfirmOpen] = useState(false);
    const pendingUnsavedActionRef = useRef(null);
    /** Pixel height of the toolbar block (686–987); table uses `max-height: calc(100vh - this)`. */
    const [catalogToolbarHeightPx, setCatalogToolbarHeightPx] = useState(null);
    const [isDefaultsBootstrapped, setIsDefaultsBootstrapped] = useState(false);
    const currentSearchValue = useDebounce(searchTerm, 300);
    const shouldSkipRequest = usePageReset(setCurrentPage, currentPage, "get:/api/v1/product-catalog");
    const resolvedDefaultColumns = mergeColumnsWithDefaults(
        productCatalogDefaultsSettings?.default_columns
    );
    const categoryFilterOptions = useMemo(
        () => (categorySettingsItems || []).map((group) => ({
            label: group.name,
            value: String(group.id),
        })),
        [categorySettingsItems]
    );
    const subcategoryFilterOptions = useMemo(() => {
        const out = [];
        (categorySettingsItems || []).forEach((group) => {
            const categorySelected = selectedCategories.length > 0;
            if (categorySelected && !selectedCategories.includes(String(group.id))) return;
            (group.variants || []).forEach((variant) => {
                out.push({
                    label: categorySelected ? variant.name : `${group.name} — ${variant.name}`,
                    value: String(variant.id),
                });
            });
        });
        return out.sort((a, b) => a.label.localeCompare(b.label));
    }, [categorySettingsItems, selectedCategories]);
    useEffect(() => {
        if (categorySettingsFetchStatus !== "succeeded" || selectedSubcategories.length === 0) return;
        const validSubcategoryIds = new Set(subcategoryFilterOptions.map((option) => option.value));
        const next = selectedSubcategories.filter((id) => validSubcategoryIds.has(String(id)));
        if (next.length !== selectedSubcategories.length) {
            setSelectedSubcategories(next);
            setCurrentPage(1);
        }
    }, [categorySettingsFetchStatus, selectedSubcategories, subcategoryFilterOptions]);
    const catalogViewOptions = useMemo(
        () => [
            ...catalogViews,
            ...customViewDefinitions.map((view) => ({
                label: view.name,
                value: view.id,
                deletable: true,
            })),
        ],
        [customViewDefinitions]
    );
    const activeCatalogView = useMemo(
        () =>
            [...customViewDefinitions, ...customViewsFromStore].find((view) => view.id === viewTable)?.base_view || viewTable,
        [customViewDefinitions, customViewsFromStore, viewTable]
    );
    const brandPillarFilterOptions = useMemo(
        () => (presetSettingsByType?.brand_pillar || []).map((item) => ({
            label: item.name,
            value: String(item.id),
        })),
        [presetSettingsByType?.brand_pillar]
    );
    const demandTierFilterOptions = useMemo(
        () => (presetSettingsByType?.demand_tier || []).map((item) => ({
            label: item.name,
            value: String(item.id),
        })),
        [presetSettingsByType?.demand_tier]
    );
    const entryEngineFilterOptions = useMemo(
        () => (presetSettingsByType?.entry_engine || []).map((item) => ({
            label: item.name,
            value: String(item.id),
        })),
        [presetSettingsByType?.entry_engine]
    );
    const intentFilterOptions = useMemo(
        () => (presetSettingsByType?.intent || []).map((item) => ({
            label: item.name,
            value: String(item.id),
        })),
        [presetSettingsByType?.intent]
    );

    const vendorSelectOptions = useMemo(
        () =>
            (vendorsToSelect || []).map((v) => ({
                label: v.label,
                value: String(v.value),
            })),
        [vendorsToSelect]
    );
    const vendorSelectLoading = groupLoadingKeys.includes(ProductGroupLoadingKeys.getSuppliers);

    const productCatalogTableRequestParams = useMemo(
        () => ({
            page: currentPage,
            itemsPerPage: currentItemsPerPage,
            search: currentSearchValue,
            statuses: selectedStatus,
            categories: selectedCategories,
            subcategories: selectedSubcategories,
            brandPillars: selectedBrandPillars,
            tiers: selectedDemandTiers,
            entryEngines: selectedEntryEngines,
            intents: selectedIntents,
            containerGroups: selectedContainerGroups,
            products: selectedProducts,
            catalogView: activeCatalogView,
            ...(sortBy ? { sortBy, sortOrder } : {}),
        }),
        [
            currentPage,
            currentItemsPerPage,
            currentSearchValue,
            selectedStatus,
            selectedCategories,
            selectedSubcategories,
            selectedBrandPillars,
            selectedDemandTiers,
            selectedEntryEngines,
            selectedIntents,
            selectedContainerGroups,
            selectedProducts,
            sortBy,
            sortOrder,
            activeCatalogView,
        ]
    );

    const refetchCatalogTable = useCallback(() => {
        dispatch(fetchProductCatalogTable(productCatalogTableRequestParams));
    }, [dispatch, productCatalogTableRequestParams]);

    const handleBulkDeleteSelected = useCallback(
        async (productIds) => {
            if (!productIds || productIds.length === 0) return;
            setBulkProductLifecycleLoading(true);
            try {
                for (const productId of productIds) {
                    await dispatch(deleteProduct({ productId })).unwrap();
                }
                refetchCatalogTable();
            } finally {
                setBulkProductLifecycleLoading(false);
            }
        },
        [dispatch, refetchCatalogTable],
    );

    const handleBulkRestoreSelected = useCallback(
        async (productIds) => {
            if (!productIds || productIds.length === 0) return;
            setBulkProductLifecycleLoading(true);
            try {
                for (const productId of productIds) {
                    await dispatch(restoreDeletedCatalogProduct({ productId })).unwrap();
                }
                refetchCatalogTable();
            } finally {
                setBulkProductLifecycleLoading(false);
            }
        },
        [dispatch, refetchCatalogTable],
    );

    const catalogTableSelectionResetKey = useMemo(
        () =>
            [
                currentPage,
                currentItemsPerPage,
                currentSearchValue,
                [...selectedStatus].sort().join(","),
                [...selectedCategories].sort().join(","),
                [...selectedSubcategories].sort().join(","),
                [...selectedBrandPillars].sort().join(","),
                [...selectedDemandTiers].sort().join(","),
                [...selectedEntryEngines].sort().join(","),
                [...selectedIntents].sort().join(","),
                [...selectedContainerGroups].sort().join(","),
                [...selectedProducts].sort().join(","),
                viewTable,
                sortBy,
                sortOrder,
            ].join("|"),
        [
            currentPage,
            currentItemsPerPage,
            currentSearchValue,
            selectedStatus,
            selectedCategories,
            selectedSubcategories,
            selectedBrandPillars,
            selectedDemandTiers,
            selectedEntryEngines,
            selectedIntents,
            selectedContainerGroups,
            selectedProducts,
            viewTable,
            sortBy,
            sortOrder,
        ]
    );

    /** Clears staged grid edits whenever the main table query changes (search, filters, page, sort, view, etc.). */
    useEffect(() => {
        setPendingCatalogEditsByProductId({});
    }, [catalogTableSelectionResetKey]);

    const handleCellDraftChange = useCallback((row, payload) => {
        if (!row?.id) return;
        setPendingCatalogEditsByProductId((prev) => ({
            ...prev,
            [row.id]: mergeCatalogPendingPayload(prev[row.id], payload),
        }));
    }, []);

    const handleConfirmSaveCatalogPendingChanges = useCallback(async () => {
        const ids = Object.keys(pendingCatalogEditsByProductId);
        if (ids.length === 0) {
            setSaveCatalogChangesConfirmOpen(false);
            return;
        }
        setSavingCatalogChanges(true);
        try {
            for (const id of ids) {
                const raw = pendingCatalogEditsByProductId[id];
                const { newVendor, ...settingsRest } = raw;
                if (newVendor !== undefined) {
                    await dispatch(
                        updateVendorByProduct({
                            productId: id,
                            newVendor,
                            skipSuccessToast: true,
                        })
                    ).unwrap();
                }
                const hasSettings = Object.keys(settingsRest).some(
                    (k) => settingsRest[k] !== undefined
                );
                if (hasSettings) {
                    await dispatch(
                        updateProductCatalogProductSettings({
                            productId: id,
                            ...settingsRest,
                            skipSuccessToast: true,
                        })
                    ).unwrap();
                }
            }
            const data = await dispatch(fetchProductCatalogTable(productCatalogTableRequestParams)).unwrap();
            setPendingCatalogEditsByProductId({});
            setSaveCatalogChangesConfirmOpen(false);
            const items = data?.items || [];
            setSelectedCatalogItem((prev) => {
                if (!prev?.id) return prev;
                const updated = items.find((r) => r.id === prev.id);
                return updated || prev;
            });
            toast.success(
                ids.length === 1
                    ? "Saved changes for 1 product."
                    : `Saved changes for ${ids.length} products.`, 
                {
                    position: "bottom-right",
                }
            );
        } catch (err) {
            const msg =
                typeof err === "string"
                    ? err
                    : err?.message ||
                      (Array.isArray(err) ? err.join(", ") : "") ||
                      "Save failed.";
            toast.error(msg);
        } finally {
            setSavingCatalogChanges(false);
        }
    }, [pendingCatalogEditsByProductId, dispatch, productCatalogTableRequestParams]);

    const pendingCatalogEditCount = Object.keys(pendingCatalogEditsByProductId).length;

    /** BrowserRouter has no DataRouterContext — `useBlocker` crashes. Intercept same-origin `<a>` clicks instead. */
    useEffect(() => {
        if (pendingCatalogEditCount === 0) return undefined;
        const handler = (e) => {
            const a = e.target?.closest?.("a[href]");
            if (!a) return;
            if (a.hasAttribute("download")) return;
            if (a.target && a.target !== "" && a.target !== "_self") return;
            if (e.defaultPrevented) return;
            const hrefAttr = a.getAttribute("href");
            if (hrefAttr == null || hrefAttr === "" || hrefAttr.startsWith("#")) return;
            if (/^(mailto:|tel:|javascript:)/i.test(hrefAttr)) return;
            let nextUrl;
            try {
                nextUrl = new URL(a.href);
            } catch {
                return;
            }
            if (nextUrl.origin !== window.location.origin) return;
            const nextKey = `${nextUrl.pathname}${nextUrl.search}`;
            const currentKey = `${location.pathname}${location.search}`;
            if (nextKey === currentKey) return;
            e.preventDefault();
            e.stopPropagation();
            pendingUnsavedActionRef.current = () => {
                navigate(`${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
            };
            setUnsavedNavigationConfirmOpen(true);
        };
        document.addEventListener("click", handler, true);
        return () => document.removeEventListener("click", handler, true);
    }, [pendingCatalogEditCount, location.pathname, location.search, navigate]);

    useEffect(() => {
        setSearchInput(searchTerm);
    }, [searchTerm]);

    useEffect(() => {
        const onBeforeUnload = (e) => {
            if (pendingCatalogEditCount > 0) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [pendingCatalogEditCount]);

    const runWithUnsavedGuard = useCallback(
        (fn) => {
            if (pendingCatalogEditCount === 0) {
                fn();
                return;
            }
            pendingUnsavedActionRef.current = fn;
            setUnsavedNavigationConfirmOpen(true);
        },
        [pendingCatalogEditCount]
    );

    const confirmDiscardUnsavedNavigation = useCallback(() => {
        setPendingCatalogEditsByProductId({});
        setUnsavedNavigationConfirmOpen(false);
        const fn = pendingUnsavedActionRef.current;
        pendingUnsavedActionRef.current = null;
        fn?.();
    }, []);

    const cancelDiscardUnsavedNavigation = useCallback(() => {
        setUnsavedNavigationConfirmOpen(false);
        pendingUnsavedActionRef.current = null;
        setSearchInput(searchTerm);
    }, [searchTerm]);

    const handleSortColumn = useCallback(
        (columnId) => {
            runWithUnsavedGuard(() => {
                setCurrentPage(1);
                if (sortBy !== columnId) {
                    setSortBy(columnId);
                    setSortOrder("asc");
                } else if (sortOrder === "asc") {
                    setSortOrder("desc");
                } else {
                    setSortBy("");
                    setSortOrder("asc");
                }
            });
        },
        [runWithUnsavedGuard, sortBy, sortOrder]
    );

    const handleExportCatalogCsv = useCallback(async () => {
        if (!isFirstLoadComplete) {
            toast.error("Catalog is still loading.");
            return;
        }
        const total = Number(tableTotal) || 0;
        if (total < 1) {
            toast.error("No products to export for the current filters.");
            return;
        }
        const itemsPerPage = Math.min(total, EXPORT_CSV_MAX_ROWS);
        if (total > EXPORT_CSV_MAX_ROWS) {
            toast.info(
                `Exporting the first ${EXPORT_CSV_MAX_ROWS.toLocaleString()} rows (export limit).`
            );
        }
        setExportingCsv(true);
        try {
            const { data } = await axios.get(PRODUCT_CATALOG_TABLE_URL, {
                params: {
                    page: 1,
                    itemsPerPage,
                    search: currentSearchValue,
                    statuses: selectedStatus,
                    categories: selectedCategories,
                    subcategories: selectedSubcategories,
                    brandPillars: selectedBrandPillars,
                    tiers: selectedDemandTiers,
                    entryEngines: selectedEntryEngines,
                    intents: selectedIntents,
                    containerGroups: selectedContainerGroups,
                    products: selectedProducts,
                    catalogView: activeCatalogView,
                    ...(sortBy ? { sortBy, sortOrder } : {}),
                },
                signal: new AbortController().signal,
            });
            const rows = data?.items || [];
            const visibleCols = (columnDefs || []).filter(
                (col) =>
                    col.visible !== false &&
                    !PRODUCT_CATALOG_EXPORT_EXCLUDED_COLUMN_IDS.includes(col.id)
            );
            if (visibleCols.length === 0) {
                toast.error("No visible columns to export.");
                return;
            }
            const headers = visibleCols.map((col) =>
                col.label != null && String(col.label).trim() !== "" ? col.label : col.id
            );
            const csvLines = [
                headers.map(escapeCsvField).join(","),
                ...rows.map((row) =>
                    visibleCols.map((col) => {
                        if (col.id === "is_protected") {
                            const value = row[col.id];
                            return escapeCsvField(value === true ? "Yes" : value === false ? "No" : "");
                        }
                        return escapeCsvField(row[col.id]);
                    }).join(",")
                ),
            ];
            const blob = new Blob([`\uFEFF${csvLines.join("\r\n")}`], {
                type: "text/csv;charset=utf-8;",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `product-catalog-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(`Exported ${rows.length.toLocaleString()} row(s).`);
        } catch (err) {
            if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
            const msg = err?.response?.data?.error || err?.response?.data?.errors || err?.message;
            toast.error(typeof msg === "string" ? msg : "Export failed.");
        } finally {
            setExportingCsv(false);
        }
    }, [
        isFirstLoadComplete,
        tableTotal,
        columnDefs,
        currentSearchValue,
        selectedStatus,
        selectedCategories,
        selectedSubcategories,
        selectedBrandPillars,
        selectedDemandTiers,
        selectedEntryEngines,
        selectedIntents,
        selectedContainerGroups,
        selectedProducts,
        sortBy,
        sortOrder,
        activeCatalogView,
    ]);

    const handleBulkUpdateSelected = useCallback(
        async (productIds, payload) => {
            for (const productId of productIds) {
                await dispatch(
                    updateProductCatalogProductSettings({
                        productId,
                        ...payload,
                        skipSuccessToast: true,
                    })
                ).unwrap();
            }
            await dispatch(fetchProductCatalogTable(productCatalogTableRequestParams));
            if (productIds.length > 0) {
                toast.success("Product settings updated successfully.", {
                    position: "bottom-right",
                });
            }
        },
        [dispatch, productCatalogTableRequestParams]
    );

    const handleBulkSectionApply = useCallback(
        async ({ productIds, vendorId, settings }) => {
            for (const productId of productIds) {
                if (vendorId !== undefined && vendorId !== null && String(vendorId).trim() !== "") {
                    await dispatch(
                        updateVendorByProduct({
                            productId,
                            newVendor: vendorId || null,
                        })
                    ).unwrap();
                }
                if (settings && typeof settings === "object" && Object.keys(settings).length > 0) {
                    await dispatch(
                        updateProductCatalogProductSettings({
                            productId,
                            ...settings,
                        })
                    ).unwrap();
                }
            }
            await dispatch(fetchProductCatalogTable(productCatalogTableRequestParams));
        },
        [dispatch, productCatalogTableRequestParams]
    );

    const handleNavigateToSkuFromCatalogSettings = useCallback(
        (sku) => {
            const trimmed = String(sku || "").trim();
            if (!trimmed) return;
            runWithUnsavedGuard(() => {
                setCatalogHubOpened(false);
                setSearchTerm(trimmed);
                setSearchInput(trimmed);
                setCurrentPage(1);
                setSelectedStatus([]);
                setSelectedCategories([]);
                setSelectedSubcategories([]);
                setSelectedBrandPillars([]);
                setSelectedDemandTiers([]);
                setSelectedEntryEngines([]);
                setSelectedIntents([]);
                setSelectedContainerGroups([]);
                setSelectedProducts([]);
                const cleared = {
                    statuses: [],
                    categories: [],
                    subcategories: [],
                    brandPillars: [],
                    tiers: [],
                    entryEngines: [],
                    intents: [],
                    containerGroups: [],
                    products: [],
                };
                dispatch(
                    fetchProductCatalogTable({
                        page: 1,
                        itemsPerPage: currentItemsPerPage,
                        search: trimmed,
                        catalogView: activeCatalogView,
                        ...cleared,
                        ...(sortBy ? { sortBy, sortOrder } : {}),
                    })
                );
                const params = new URLSearchParams({
                    viewTable,
                    search: trimmed,
                    page: 1,
                    itemsPerPage: currentItemsPerPage,
                    ...cleared,
                    ...(sortBy ? { sortBy, sortOrder } : {}),
                });
                navigate(`${ROUTES.PRODUCTS_CATALOG}?${params}`, { replace: true });
            });
        },
        [dispatch, currentItemsPerPage, navigate, runWithUnsavedGuard, sortBy, sortOrder, activeCatalogView]
    );

    const handleApplyCatalogSettingFilter = useCallback(
        ({ kind, id }) => {
            const sid = String(id ?? "").trim();
            if (!sid || !kind) return;
            runWithUnsavedGuard(() => {
                setCatalogHubOpened(false);
                setSearchTerm("");
                setSearchInput("");
                setCurrentPage(1);
                setSelectedStatus([]);
                setSelectedCategories([]);
                setSelectedSubcategories([]);
                setSelectedBrandPillars([]);
                setSelectedDemandTiers([]);
                setSelectedEntryEngines([]);
                setSelectedIntents([]);
                setSelectedContainerGroups([]);
                setSelectedProducts([]);

                const next = {
                    statuses: [],
                    categories: [],
                    subcategories: [],
                    brandPillars: [],
                    tiers: [],
                    entryEngines: [],
                    intents: [],
                    containerGroups: [],
                    products: [],
                };
                switch (kind) {
                    case "category_group":
                        next.categories = [sid];
                        setSelectedCategories([sid]);
                        break;
                    case "category_variant":
                        next.subcategories = [sid];
                        setSelectedSubcategories([sid]);
                        break;
                    case "entry_engine":
                        next.entryEngines = [sid];
                        setSelectedEntryEngines([sid]);
                        break;
                    case "brand_pillar":
                        next.brandPillars = [sid];
                        setSelectedBrandPillars([sid]);
                        break;
                    case "demand_tier":
                        next.tiers = [sid];
                        setSelectedDemandTiers([sid]);
                        break;
                    case "intent":
                        next.intents = [sid];
                        setSelectedIntents([sid]);
                        break;
                    case "container_group":
                        next.containerGroups = [sid];
                        setSelectedContainerGroups([sid]);
                        break;
                    default:
                        return;
                }

                dispatch(
                    fetchProductCatalogTable({
                        page: 1,
                        itemsPerPage: currentItemsPerPage,
                        search: "",
                        catalogView: activeCatalogView,
                        statuses: next.statuses,
                        categories: next.categories,
                        subcategories: next.subcategories,
                        brandPillars: next.brandPillars,
                        tiers: next.tiers,
                        entryEngines: next.entryEngines,
                        intents: next.intents,
                        containerGroups: next.containerGroups,
                        products: next.products,
                        ...(sortBy ? { sortBy, sortOrder } : {}),
                    })
                );
                const params = new URLSearchParams({
                    viewTable,
                    search: "",
                    page: 1,
                    itemsPerPage: currentItemsPerPage,
                    statuses: next.statuses,
                    categories: next.categories,
                    subcategories: next.subcategories,
                    brandPillars: next.brandPillars,
                    tiers: next.tiers,
                    entryEngines: next.entryEngines,
                    intents: next.intents,
                    containerGroups: next.containerGroups,
                    products: next.products,
                    ...(sortBy ? { sortBy, sortOrder } : {}),
                });
                navigate(`${ROUTES.PRODUCTS_CATALOG}?${params}`, { replace: true });
            });
        },
        [dispatch, currentItemsPerPage, navigate, runWithUnsavedGuard, sortBy, sortOrder, activeCatalogView]
    );

    useEffect(() => {
        dispatch(getSuppliers({}));
    }, [dispatch]);

    useEffect(() => {
        dispatch(fetchProductCatalogDefaultsSettings());
        dispatch(fetchTierDiscountDrivers());
    }, [dispatch]);

    useEffect(() => {
        if (!["succeeded", "failed"].includes(defaultsSettingsFetchStatus)) return;
        if (didRunPostDefaultsRequests.current) return;
        didRunPostDefaultsRequests.current = true;
        dispatch(fetchProductCatalogColumnsMetadata());
        dispatch(fetchProductCatalogCategorySettings());
        dispatch(fetchProductCatalogPresetSettings());
        dispatch(fetchProductCatalogContainerSettings());
    }, [defaultsSettingsFetchStatus, dispatch]);

    useEffect(() => {
        catalogTableRef.current?.scrollToTop?.();
    }, [currentPage, currentItemsPerPage, sortBy, sortOrder]);

    useEffect(() => {
        if (!isDefaultsBootstrapped) return;
        if (currentPage === 1 && shouldSkipRequest()) {
            const params = new URLSearchParams({
                viewTable,
                search: currentSearchValue,
                page: currentPage,
                itemsPerPage: currentItemsPerPage,
                statuses: selectedStatus,
                categories: selectedCategories,
                subcategories: selectedSubcategories,
                brandPillars: selectedBrandPillars,
                tiers: selectedDemandTiers,
                entryEngines: selectedEntryEngines,
                intents: selectedIntents,
                containerGroups: selectedContainerGroups,
                products: selectedProducts,
                ...(sortBy ? { sortBy, sortOrder } : {}),
            });
            navigate(`${ROUTES.PRODUCTS_CATALOG}?${params}`, { replace: true });
            return;
        }
        dispatch(fetchProductCatalogTable(productCatalogTableRequestParams));
        const params = new URLSearchParams({
            viewTable,
            search: currentSearchValue,
            page: currentPage,
            itemsPerPage: currentItemsPerPage,
            statuses: selectedStatus,
            categories: selectedCategories,
            subcategories: selectedSubcategories,
            brandPillars: selectedBrandPillars,
            tiers: selectedDemandTiers,
            entryEngines: selectedEntryEngines,
            intents: selectedIntents,
            containerGroups: selectedContainerGroups,
            products: selectedProducts,
            ...(sortBy ? { sortBy, sortOrder } : {}),
        });
        navigate(`${ROUTES.PRODUCTS_CATALOG}?${params}`, { replace: true });
    }, [
        currentItemsPerPage,
        currentPage,
        currentSearchValue,
        dispatch,
        isDefaultsBootstrapped,
        navigate,
        productCatalogTableRequestParams,
        selectedStatus,
        selectedCategories,
        selectedSubcategories,
        selectedBrandPillars,
        selectedDemandTiers,
        selectedEntryEngines,
        selectedIntents,
        selectedContainerGroups,
        selectedProducts,
        shouldSkipRequest,
        sortBy,
        sortOrder,
        viewTable,
    ]);

    useEffect(() => {
        if (!["succeeded", "failed"].includes(defaultsSettingsFetchStatus)) return;
        if (didHydrateDefaultsFromApi.current) return;
        didHydrateDefaultsFromApi.current = true;
        if (!hasItemsPerPageInInitialUrl.current) {
            setCurrentItemsPerPage(
                Number(productCatalogDefaultsSettings?.default_pagination_pages) || 50
            );
        }
        setIsDefaultsBootstrapped(true);
    }, [defaultsSettingsFetchStatus, productCatalogDefaultsSettings?.default_pagination_pages]);

    useEffect(() => {
        if (columnsMetadataFetchStatus !== "succeeded") return;
        if (!["succeeded", "failed"].includes(defaultsSettingsFetchStatus)) return;
        if (didHydrateFromApi.current) return;
        didHydrateFromApi.current = true;
        setSavedColumnDefsByView(normalizeCatalogColumnsByView(byViewFromStore));
        setCustomViewDefinitions(normalizeProductCatalogCustomViews(customViewsFromStore));
        setColumnDefs(
            cloneColumns(mergeColumnsWithDefaults(byViewFromStore[viewTable] || resolvedDefaultColumns))
        );
    }, [columnsMetadataFetchStatus, defaultsSettingsFetchStatus, byViewFromStore, customViewsFromStore, resolvedDefaultColumns, viewTable]);

    const handleViewTableChange = useCallback(
        (newView) => {
            runWithUnsavedGuard(() => {
                setViewTable(newView);
                setColumnDefs(
                    cloneColumns(mergeColumnsWithDefaults(savedColumnDefsByView[newView] || resolvedDefaultColumns))
                );
            });
        },
        [resolvedDefaultColumns, runWithUnsavedGuard, savedColumnDefsByView]
    );

    const handleSaveCurrentView = () => {
        setSaveViewModalOpened(true);
    };

    const handleSaveNamedView = (name) => {
        const normalizedName = name.trim().toLowerCase();
        const matchesPreset = catalogViews.some(
            (view) => view.label.trim().toLowerCase() === normalizedName
        );
        if (matchesPreset) {
            toast.error("A view with that name already exists.", { position: "bottom-right" });
            return;
        }
        const existingCustomView = customViewDefinitions.find(
            (view) => view.name.trim().toLowerCase() === normalizedName
        );
        const id = existingCustomView?.id || `custom_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const nextCustomViews = existingCustomView
            ? customViewDefinitions
            : [
                  ...customViewDefinitions,
                  { id, name: name.trim(), base_view: activeCatalogView },
              ];
        const nextSaved = {
            ...savedColumnDefsByView,
            [id]: cloneColumns(normalizeCatalogColumns(columnDefs)),
        };
        setCustomViewDefinitions(nextCustomViews);
        setSavedColumnDefsByView(nextSaved);
        setViewTable(id);
        setColumnDefs(cloneColumns(mergeColumnsWithDefaults(nextSaved[id])));
        setSaveViewModalOpened(false);
        setColumnsSettingsOpened(false);
        dispatch(saveProductCatalogColumnsMetadata({
            by_view: nextSaved,
            custom_views: nextCustomViews,
        }));
    };

    const handleRequestDeleteCustomView = (option) => {
        const view = customViewDefinitions.find((candidate) => candidate.id === option.value);
        if (!view) return;
        setCustomViewToDelete(view);
        setDeleteCustomViewModalOpened(true);
    };

    const handleDeleteCustomView = () => {
        if (!customViewToDelete) return;
        const nextCustomViews = customViewDefinitions.filter((view) => view.id !== customViewToDelete.id);
        const nextSaved = { ...savedColumnDefsByView };
        delete nextSaved[customViewToDelete.id];
        setCustomViewDefinitions(nextCustomViews);
        setSavedColumnDefsByView(nextSaved);
        if (viewTable === customViewToDelete.id) {
            const nextView = customViewToDelete.base_view || "default";
            setViewTable(nextView);
            setColumnDefs(
                cloneColumns(mergeColumnsWithDefaults(nextSaved[nextView] || resolvedDefaultColumns))
            );
        }
        setCustomViewToDelete(null);
        setDeleteCustomViewModalOpened(false);
        dispatch(saveProductCatalogColumnsMetadata({
            by_view: nextSaved,
            custom_views: nextCustomViews,
        }));
    };

    const handleSaveDefaultSettings = async (settings) => {
        await dispatch(saveProductCatalogDefaultsSettings(settings));
        setCatalogHubOpened(false);
        if (!hasItemsPerPageInInitialUrl.current) {
            runWithUnsavedGuard(() => {
                setCurrentItemsPerPage(Number(settings?.default_pagination_pages) || 50);
            });
        }
    };

    const handleSaveViewPresets = async (nextViewPresets) => {
        const normalizedPresets = normalizeCatalogColumnsByView(nextViewPresets);
        setSavedColumnDefsByView(normalizedPresets);
        await dispatch(saveProductCatalogColumnsMetadata({
            by_view: normalizedPresets,
            custom_views: customViewDefinitions,
        }));
        if (normalizedPresets?.[viewTable]) {
            setColumnDefs(
                cloneColumns(
                    mergeColumnsWithDefaults(normalizedPresets[viewTable] || resolvedDefaultColumns)
                )
            );
        }
    };

    const handleSaveTierDiscountDrivers = async (drivers) => {
        await dispatch(saveTierDiscountDrivers(drivers));
    };

    const withActiveFilters =
        searchTerm.length > 0 ||
        arrayHasItems(selectedStatus) ||
        arrayHasItems(selectedCategories) ||
        arrayHasItems(selectedSubcategories) ||
        arrayHasItems(selectedBrandPillars) ||
        arrayHasItems(selectedDemandTiers) ||
        arrayHasItems(selectedEntryEngines) ||
        arrayHasItems(selectedIntents) ||
        arrayHasItems(selectedContainerGroups) ||
        arrayHasItems(selectedProducts);
    const isFetchProductCatalogTableLoading = () => loadingKeys.includes(ProductCatalogLoadingKeys.fetchTable);
    const isFetchProductCatalogColumnsMetadataLoading = () => loadingKeys.includes(ProductCatalogLoadingKeys.fetchColumnsMetadata);
    const isCatalogTableLoading =
        !isFirstLoadComplete ||
        isFetchProductCatalogTableLoading() ||
        isFetchProductCatalogColumnsMetadataLoading();
    const isUpdateProductCatalogSettingsLoading = () => loadingKeys.includes(ProductCatalogLoadingKeys.updateProductSettings);
    const isFetchProductCatalogCategorySettingsLoading = () => loadingKeys.includes(ProductCatalogLoadingKeys.fetchCategorySettings);
    const isFetchProductCatalogPresetSettingsLoading = () => loadingKeys.includes(ProductCatalogLoadingKeys.fetchPresetSettings);
    const isDeleteProductLoading = () => productCostsLoadingKeys.includes(ProductCostsLoadingKeys.deleteProduct);
    const isRestoreProductLoading = () => loadingKeys.includes(ProductCatalogLoadingKeys.restoreDeletedProduct);

    const measureCatalogToolbarHeight = useCallback(() => {
        const el = catalogToolbarRef.current;
        if (!el) return;
        const h = Math.round(el.getBoundingClientRect().height);
        setCatalogToolbarHeightPx(Math.max(0, h));
    }, []);

    useLayoutEffect(() => {
        measureCatalogToolbarHeight();
        const toolbar = catalogToolbarRef.current;
        const ro =
            typeof ResizeObserver !== "undefined"
                ? new ResizeObserver(() => measureCatalogToolbarHeight())
                : null;
        if (toolbar && ro) ro.observe(toolbar);
        window.addEventListener("resize", measureCatalogToolbarHeight);
        return () => {
            window.removeEventListener("resize", measureCatalogToolbarHeight);
            ro?.disconnect();
        };
    }, [measureCatalogToolbarHeight]);

    return (
        <>
            <main className="h-full w-full p-4 sm:p-8">
                <div className="h-full flex flex-col gap-2">
                    <div ref={catalogToolbarRef} className="flex shrink-0 flex-col gap-2">
                    <section className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex min-w-0 shrink-0 items-baseline gap-2">
                            <h2 className="text-[14px] sm:text-[20px] font-bold tracking-tight text-gray-900 leading-none whitespace-nowrap">
                                Product Catalog
                            </h2>
                            <p className="hidden sm:inline text-[13px] text-gray-400 tabular-nums whitespace-nowrap">
                                {`${tableTotal} products`}
                            </p>
                        </div>
                        <div className="flex flex-row items-center gap-2">
                            <div className="w-[240px] relative hidden sm:block">
                                <SelectDropdown
                                    options={catalogViewOptions}
                                    value={viewTable}
                                    onChange={handleViewTableChange}
                                    showSaveCurrentView={true}
                                    onDeleteOption={handleRequestDeleteCustomView}
                                    SelectedValueIconComponent={() => {
                                        return (
                                            <Squares2X2Icon className="h-5 w-5 text-primary" />
                                        )
                                    }}
                                    onSaveCurrentView={handleSaveCurrentView}
                                    selectClassname="!px-3 !py-1.5 !bg-white !text-primary !rounded-md !text-sm !transition-all !duration-200 hover:!bg-gray-100"
                                    selectedValueClassname="!text-primary !truncate"
                                    selectedItemValueClassname="!bg-white"
                                    dropdownClassname="!z-[150]"
                                    dropdownListClassname="!max-h-[550px]"
                                    renderOption={(option) => (
                                        <article className="flex flex-row items-center gap-2">
                                            {option.value === viewTable ? (
                                                <StarIconSolid className="h-5 w-5 text-yellow-400" />
                                            ) : (
                                                <StarIconOutline className="h-5 w-5" />
                                            )}
                                            <p className="text-sm font-medium text-primary">{option.label}</p>
                                        </article>
                                    )}
                                />
                            </div>
                            <button 
                                onClick={() => {
                                    setColumnsSettingsOpened(true);
                                }}
                                className="hidden sm:flex h-8 flex-row items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 transition-colors hover:bg-gray-50"
                            >
                                <ViewColumnsIcon className="h-4 w-4 text-gray-400" />
                                <p className="text-[13px] font-medium">Columns</p>
                            </button>
                            <button
                                type="button"
                                disabled={exportingCsv || !isFirstLoadComplete || !tableTotal}
                                onClick={handleExportCatalogCsv}
                                className="hidden sm:flex relative h-8 flex-row items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                            >
                                {exportingCsv && (
                                    <div className="absolute left-0 top-0 w-full h-full flex items-center justify-center">
                                        <LoaderSpinner width="20" height="20" colorSpinner="black" border="2" />
                                    </div>
                                )}
                                <ArrowDownTrayIcon className="h-4 w-4 shrink-0 text-gray-400" />
                                <span className="text-[13px] font-medium text-inherit">
                                    {exportingCsv ? "Exporting…" : "Export"}
                                </span>
                            </button>
                            <button 
                                className="flex h-8 flex-row items-center gap-1 rounded-md bg-gray-900 px-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-gray-800 whitespace-nowrap"
                                onClick={() => {
                                    setAddProductOpened(true);
                                }}
                            >
                                <PlusIcon className="h-3.5 w-3.5 shrink-0 text-white" />
                                <p className="hidden sm:inline text-[13px] font-semibold text-white">Add Product</p>
                                <p className="sm:hidden text-[13px] font-semibold text-white">Add</p>
                            </button>
                            <button
                                type="button"
                                disabled={pendingCatalogEditCount === 0 || savingCatalogChanges}
                                onClick={() => setSaveCatalogChangesConfirmOpen(true)}
                                className="hidden sm:flex relative h-8 flex-row items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                            >
                                {savingCatalogChanges && (
                                    <div className="absolute left-0 top-0 w-full h-full flex items-center justify-center">
                                        <LoaderSpinner width="20" height="20" colorSpinner="black" border="2" />
                                    </div>
                                )}
                                <RectangleStackIcon className="h-4 w-4 text-gray-400" />
                                <p className="text-[13px] font-medium">
                                    {pendingCatalogEditCount > 0
                                        ? `Save Changes (${pendingCatalogEditCount})`
                                        : "Save Changes"}
                                </p>
                            </button>
                            <button
                                type="button"
                                onClick={() => setCatalogHistoryOpened(true)}
                                className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-400 transition-colors hover:bg-gray-50"
                                aria-label="Catalog change history"
                            >
                                <ClockIcon className="h-4 w-4" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setCatalogHubOpened(true)}
                                className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-400 transition-colors hover:bg-gray-50"
                                aria-label="Catalog settings"
                            >
                                <Cog8ToothIcon className="h-4 w-4" />
                            </button>
                        </div>
                    </section>
                    <div className="h-[1px] bg-gray-300" />
                    <section className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                        <div className="flex min-w-0 w-full flex-col gap-2 p-1 pl-0 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">
                            <div className="relative w-full shrink-0 sm:w-72">
                                <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search SKU, name, barcode…"
                                    value={searchInput}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        runWithUnsavedGuard(() => {
                                            setCurrentPage(1);
                                            setSearchTerm(v);
                                            setSearchInput(v);
                                        });
                                        if (pendingCatalogEditCount > 0) {
                                            setSearchInput(v);
                                        }
                                    }}
                                    className={`h-8 w-full transition-colors rounded-md border border-gray-200 bg-white pl-8 text-[13px] placeholder:text-gray-400 outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-300 ${searchTerm ? "pr-8" : "pr-3"}`}
                                />
                                {searchTerm ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            runWithUnsavedGuard(() => {
                                                setCurrentPage(1);
                                                setSearchTerm("");
                                                setSearchInput("");
                                            });
                                        }}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-800"
                                        aria-label="Clear search"
                                    >
                                        <XMarkIcon className="h-3.5 w-3.5" />
                                    </button>
                                ) : null}
                            </div>
                            <div className="flex min-w-0 items-center gap-2 overflow-x-auto overflow-y-visible sm:contents">
                            <ProductCatalogSelectFilter
                                compact
                                activePillStyling
                                renderInPortal
                                allLabel="All categories"
                                // noneLabel="Skip categories"
                                options={categoryFilterOptions}
                                selectedOptions={selectedCategories}
                                isLoading={isFetchProductCatalogCategorySettingsLoading()}
                                onChange={(newCategories) => {
                                    runWithUnsavedGuard(() => {
                                        setCurrentPage(1);
                                        setSelectedCategories(newCategories);
                                        if (newCategories.length > 0) {
                                            const validSubcategoryIds = new Set(
                                                (categorySettingsItems || [])
                                                    .filter((group) => newCategories.includes(String(group.id)))
                                                    .flatMap((group) =>
                                                        (group.variants || []).map((variant) => String(variant.id))
                                                    )
                                            );
                                            setSelectedSubcategories((current) =>
                                                current.filter((id) => validSubcategoryIds.has(String(id)))
                                            );
                                        }
                                    });
                                }}
                                renderOption={(option) => (
                                    <span className="text-gray-900">{option.label}</span>
                                )}
                            />
                            <ProductCatalogSelectFilter
                                compact
                                activePillStyling
                                renderInPortal
                                allLabel="All subcategories"
                                options={subcategoryFilterOptions}
                                selectedOptions={selectedSubcategories}
                                isLoading={isFetchProductCatalogCategorySettingsLoading()}
                                onChange={(next) => {
                                    runWithUnsavedGuard(() => {
                                        setCurrentPage(1);
                                        setSelectedSubcategories(next);
                                    });
                                }}
                                renderOption={(option) => (
                                    <span className="text-gray-900">{option.label}</span>
                                )}
                            />
                            <ProductCatalogSelectFilter
                                compact
                                activePillStyling
                                renderInPortal
                                allLabel="All statuses"
                                // noneLabel="Skip statuses"
                                options={productCatalogStatuses}
                                selectedOptions={selectedStatus}
                                onChange={(newStatuses) => {
                                    runWithUnsavedGuard(() => {
                                        setCurrentPage(1);
                                        setSelectedStatus(newStatuses);
                                    });
                                }}
                                renderOption={(phase) => (
                                    <span className="text-gray-900">{phase.label}</span>
                                )}
                            />
                            {/* <ProductCatalogSelectFilter
                                activePillStyling
                                renderInPortal
                                allLabel="All entry engines"
                                noneLabel="No entry engines"
                                options={entryEngineFilterOptions}
                                selectedOptions={selectedEntryEngines}
                                isLoading={isFetchProductCatalogCategorySettingsLoading()}
                                onChange={(next) => {
                                    setCurrentPage(1);
                                    setSelectedEntryEngines(next);
                                }}
                                renderOption={(option) => (
                                    <span className="text-gray-900">{option.label}</span>
                                )}
                            /> */}
                            <ProductCatalogSelectFilter
                                compact
                                activePillStyling
                                renderInPortal
                                allLabel="All pillars"
                                // noneLabel="Skip pillars"
                                options={brandPillarFilterOptions}
                                selectedOptions={selectedBrandPillars}
                                isLoading={isFetchProductCatalogCategorySettingsLoading()}
                                onChange={(newPillars) => {
                                    runWithUnsavedGuard(() => {
                                        setCurrentPage(1);
                                        setSelectedBrandPillars(newPillars);
                                    });
                                }}
                                renderOption={(option) => (
                                    <span className="text-gray-900">{option.label}</span>
                                )}
                            />
                            <ProductCatalogSelectFilter
                                compact
                                activePillStyling
                                renderInPortal
                                allLabel="All tiers"
                                // noneLabel="Skip tiers"
                                options={demandTierFilterOptions}
                                selectedOptions={selectedDemandTiers}
                                isLoading={isFetchProductCatalogCategorySettingsLoading()}
                                onChange={(newTiers) => {
                                    runWithUnsavedGuard(() => {
                                        setCurrentPage(1);
                                        setSelectedDemandTiers(newTiers);
                                    });
                                }}
                                renderOption={(option) => (
                                    <span className="text-gray-900">{option.label}</span>
                                )}
                            />
                            <ProductCatalogSelectFilter
                                compact
                                activePillStyling
                                renderInPortal
                                allLabel="All Products"
                                // noneLabel="Skip Products"
                                buttonSelectedClassname={`${productCatalogProducts.find((product) => product.value === selectedProducts[0] && selectedProducts.length !== productCatalogProducts.length) ? "!border-amber-400 !text-amber-700 !bg-amber-50 focus:!ring-amber-400" : ""}`}
                                selectClassname={`w-[200px]`}
                                options={productCatalogProducts}
                                selectedOptions={selectedProducts}
                                onChange={(newProducts) => {
                                    runWithUnsavedGuard(() => {
                                        setCurrentPage(1);
                                        setSelectedProducts(newProducts);
                                    });
                                }}
                                renderOption={(option) => (
                                    <span className="text-gray-900">{option.label}</span>
                                )}
                            />
                            {/* <ProductCatalogSelectFilter
                                activePillStyling
                                renderInPortal
                                allLabel="All intents"
                                // noneLabel="No intents"
                                options={intentFilterOptions}
                                selectedOptions={selectedIntents}
                                isLoading={isFetchProductCatalogCategorySettingsLoading()}
                                onChange={(next) => {
                                    setCurrentPage(1);
                                    setSelectedIntents(next);
                                }}
                                renderOption={(option) => (
                                    <span className="text-gray-900">{option.label}</span>
                                )}
                            /> */}
                            {withActiveFilters && (
                                <button type="button" className="flex shrink-0 flex-row items-center gap-2 cursor-pointer hover:bg-gray-100 rounded-md px-2 py-2 transition-all duration-200" onClick={() => {
                                    runWithUnsavedGuard(() => {
                                        setCurrentPage(1);
                                        setSelectedStatus([]);
                                        setSelectedCategories([]);
                                        setSelectedSubcategories([]);
                                        setSelectedBrandPillars([]);
                                        setSelectedDemandTiers([]);
                                        setSelectedEntryEngines([]);
                                        setSelectedIntents([]);
                                        setSelectedContainerGroups([]);
                                        setSelectedProducts([]);
                                        setSearchTerm("");
                                        setSearchInput("");
                                    });
                                }}>
                                    <ArrowPathIcon className="h-5 w-5 text-primary" />
                                    <p className="text-sm font-medium text-primary">Clear</p>
                                </button>
                            )}
                            </div>
                        </div>
                        <div className="flex shrink-0 flex-row items-center gap-2 sm:justify-end p-1 pr-0">
                            <p className="text-xs text-gray-500 whitespace-nowrap text-center">
                                {tablePagination?.rangeRows || "0-0 of 0"}
                            </p>
                            {tableItems.length > 0 && isCatalogTableLoading && (
                                <ArrowPathIcon
                                    className="h-3.5 w-3.5 animate-spin text-gray-500"
                                    aria-label="Refreshing product catalog"
                                />
                            )}
                            <p className="hidden text-xs text-gray-500 sm:block">
                                Rows:
                            </p>
                            <ProductCatalogPageSizeSelect
                                className="hidden sm:block"
                                value={currentItemsPerPage}
                                onChange={(next) => {
                                    runWithUnsavedGuard(() => {
                                        setCurrentPage(1);
                                        setCurrentItemsPerPage(next);
                                    });
                                }}
                                testId="product-catalog-page-size"
                            />
                            {/* <button
                                type="button"
                                disabled={(tablePagination?.currentPage || 1) === 1}
                                onClick={() => setCurrentPage(1)}
                                className="rounded-md bg-white p-1.5 text-gray-700 transition-all duration-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                            >
                                <ChevronDoubleLeftIcon className="h-4 w-4" />
                            </button> */}
                            <button
                                type="button"
                                disabled={(tablePagination?.currentPage || 1) === 1}
                                onClick={() =>
                                    runWithUnsavedGuard(() =>
                                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                                    )
                                }
                                className="rounded-md bg-white p-1.5 text-gray-700 transition-all duration-200 hover:bg-gray-100 disabled:cursor-not-allowed"
                            >
                                <ChevronLeftIcon className="h-3 w-3" />
                            </button>
                            <p className="text-xs text-gray-500 whitespace-nowrap text-center">
                                {`${tablePagination?.currentPage || 1}/${tablePagination?.totalPages || 1}`}
                            </p>
                            <button
                                type="button"
                                disabled={tablePagination?.isTheLastPage}
                                onClick={() =>
                                    runWithUnsavedGuard(() => setCurrentPage((prev) => prev + 1))
                                }
                                className="rounded-md bg-white p-1.5 text-gray-700 transition-all duration-200 hover:bg-gray-100 disabled:cursor-not-allowed"
                            >
                                <ChevronRightIcon className="h-3 w-3" />
                            </button>
                            {/* <button
                                type="button"
                                disabled={tablePagination?.isTheLastPage}
                                onClick={() => setCurrentPage(tablePagination?.totalPages || 1)}
                                className="rounded-md bg-white p-1.5 text-gray-700 transition-all duration-200 hover:bg-gray-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
                            >
                                <ChevronDoubleRightIcon className="h-4 w-4" />
                            </button> */}
                        </div>
                    </section>
                    </div>
                    <section className="flex h-full min-h-0 flex-1 flex-col">
                        <ProductCatalogTable
                            ref={catalogTableRef}
                            toolbarBlockHeightPx={catalogToolbarHeightPx}
                            items={tableItems}
                            columns={columnDefs}
                            categorySettingsItems={categorySettingsItems}
                            presetSettingsByType={presetSettingsByType}
                            containerSettingsItems={containerSettingsItems}
                            vendorSelectOptions={vendorSelectOptions}
                            vendorSelectLoading={vendorSelectLoading}
                            isLoading={tableItems.length === 0 && isCatalogTableLoading}
                            selectionResetKey={catalogTableSelectionResetKey}
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSortColumn={handleSortColumn}
                            onBulkUpdateSelected={handleBulkUpdateSelected}
                            onBulkSectionApply={handleBulkSectionApply}
                            isBulkUpdating={isUpdateProductCatalogSettingsLoading()}
                            onCellDraftChange={handleCellDraftChange}
                            catalogView={activeCatalogView}
                            onBulkDeleteSelected={handleBulkDeleteSelected}
                            onBulkRestoreSelected={handleBulkRestoreSelected}
                            isBulkDeleteOrRestoreBusy={bulkProductLifecycleLoading}
                            onEditItem={(item) => {
                                setSelectedCatalogItem(item);
                                setProductDetailsOpened(true);
                            }}
                        />
                    </section>
                </div>
            </main>
            <ProductCatalogColumnsSettings
                isOpen={columnsSettingsOpened}
                onClose={() => {
                    setColumnsSettingsOpened(false);
                }}
                columns={columnDefs}
                defaultColumns={resolvedDefaultColumns}
                onChangeColumns={setColumnDefs}
                onSaveCurrentView={handleSaveCurrentView}
            />
            <ProductCatalogSaveViewModal
                isOpen={saveViewModalOpened}
                initialName=""
                onClose={() => setSaveViewModalOpened(false)}
                onSave={handleSaveNamedView}
            />
            <ConfrimModal
                isOpen={deleteCustomViewModalOpened}
                title="Delete custom view?"
                description={`Delete "${customViewToDelete?.name || ""}"? Its saved column layout will be removed.`}
                cancelClick={() => {
                    setDeleteCustomViewModalOpened(false);
                    setCustomViewToDelete(null);
                }}
                submitClick={handleDeleteCustomView}
                confirmButtonTitle="Delete"
                size="max-w-lg"
            />
            <ProductCatalogCatalogHubDrawer
                isOpen={catalogHubOpened}
                onClose={() => setCatalogHubOpened(false)}
                refetchCatalogTable={refetchCatalogTable}
                onNavigateToSkuSearch={handleNavigateToSkuFromCatalogSettings}
                onApplyCatalogSettingFilter={handleApplyCatalogSettingFilter}
                productCatalogDefaultsSettings={productCatalogDefaultsSettings}
                tierDiscountDrivers={tierDiscountDrivers}
                demandTierPresets={presetSettingsByType?.demand_tier || []}
                viewPresets={savedColumnDefsByView}
                onSaveDefaults={handleSaveDefaultSettings}
                onSaveViewPresets={handleSaveViewPresets}
                onSaveTierDiscountDrivers={handleSaveTierDiscountDrivers}
            />
            <ProductCatalogProductSidebar
                isOpen={productDetailsOpened}
                item={selectedCatalogItem}
                isSaving={isUpdateProductCatalogSettingsLoading()}
                catalogView={activeCatalogView}
                productIdToDelete={productIdToDelete}
                isDeleteProductLoading={isDeleteProductLoading}
                productIdToRestore={productIdToRestore}
                isRestoreProductLoading={isRestoreProductLoading}
                onDeleteClick={(item) => {
                    if (!item?.id) return;
                    setProductIdToDelete(item.id);
                    setDeleteProductModalOpened(true);
                }}
                onRestoreClick={(item) => {
                    if (!item?.id) return;
                    setProductIdToRestore(item.id);
                    setRestoreProductModalOpened(true);
                }}
                onHistoryClick={() => setProductHistoryOpened(true)}
                onSave={async (settings) => {
                    if (!selectedCatalogItem?.id) return;
                    const { vendor_id, ...catalogSettings } = settings;
                    const nextVendor =
                        vendor_id !== undefined &&
                        vendor_id !== null &&
                        String(vendor_id).trim() !== ""
                            ? String(vendor_id).trim()
                            : null;
                    const currentVendor =
                        selectedCatalogItem?.vendor_id !== undefined &&
                        selectedCatalogItem?.vendor_id !== null &&
                        String(selectedCatalogItem.vendor_id).trim() !== ""
                            ? String(selectedCatalogItem.vendor_id).trim()
                            : null;
                    if (nextVendor !== currentVendor) {
                        await dispatch(
                            updateVendorByProduct({
                                productId: selectedCatalogItem.id,
                                newVendor: nextVendor,
                            })
                        ).unwrap();
                    }
                    const res = await dispatch(
                        updateProductCatalogProductSettings({
                            productId: selectedCatalogItem.id,
                            ...catalogSettings,
                        })
                    ).unwrap();
                    setSelectedCatalogItem((prev) =>
                        mergeSelectedCatalogItemWithSettingsResponse(prev, settings, res?.item)
                    );
                    setProductDetailsOpened(false);
                    refetchCatalogTable();
                }}
                onClose={() => {
                    setProductDetailsOpened(false);
                    setSelectedCatalogItem(null);
                }}
            />
            <ProductCatalogHistoryDrawer
                isOpen={productHistoryOpened}
                item={selectedCatalogItem}
                onClose={() => setProductHistoryOpened(false)}
                onReverted={refetchCatalogTable}
            />
            <ProductCatalogHistoryDrawer
                isOpen={catalogHistoryOpened}
                catalogWide
                onClose={() => setCatalogHistoryOpened(false)}
                onReverted={refetchCatalogTable}
            />
            <ConfrimModal
                isOpen={deleteProductModalOpened}
                title={"Are you sure want to delete this product?"}
                confirmButtonTitle={"Delete"}
                isLoading={isDeleteProductLoading()}
                submitClick={async () => {
                    if (!productIdToDelete) return;
                    setDeleteProductModalOpened(false);
                    await dispatch(deleteProduct({ productId: productIdToDelete })).unwrap();
                    setProductIdToDelete("");
                    setProductDetailsOpened(false);
                    setSelectedCatalogItem(null);
                    refetchCatalogTable();
                }}
                cancelClick={() => {
                    setDeleteProductModalOpened(false);
                    setProductIdToDelete("");
                }}
            />
            <ConfrimModal
                isOpen={restoreProductModalOpened}
                title="Restore this product?"
                confirmButtonTitle="Restore"
                isLoading={isRestoreProductLoading()}
                submitClick={async () => {
                    if (!productIdToRestore) return;
                    setRestoreProductModalOpened(false);
                    try {
                        await dispatch(
                            restoreDeletedCatalogProduct({ productId: productIdToRestore })
                        ).unwrap();
                        setProductIdToRestore("");
                        setProductDetailsOpened(false);
                        setSelectedCatalogItem(null);
                        refetchCatalogTable();
                    } catch {
                        setProductIdToRestore("");
                    }
                }}
                cancelClick={() => {
                    setRestoreProductModalOpened(false);
                    setProductIdToRestore("");
                }}
                size="max-w-lg"
                testId="product-catalog-restore-product"
            />
            <ProductCatalogAddProductDrawer
                isOpen={addProductOpened}
                onClose={() => setAddProductOpened(false)}
                onProductCreated={refetchCatalogTable}
                categorySettingsItems={categorySettingsItems}
                presetSettingsByType={presetSettingsByType}
                containerSettingsItems={containerSettingsItems}
            />
            <ConfrimModal
                isOpen={saveCatalogChangesConfirmOpen}
                isLoading={savingCatalogChanges}
                title="Are you sure you want to save changes?"
                confirmButtonTitle="Save"
                submitClick={handleConfirmSaveCatalogPendingChanges}
                cancelClick={() => {
                    if (!savingCatalogChanges) setSaveCatalogChangesConfirmOpen(false);
                }}
                size="max-w-lg"
                testId="product-catalog-save-pending-changes"
            />
            <ConfrimModal
                isOpen={unsavedNavigationConfirmOpen}
                isLoading={false}
                title="You have unsaved changes in the grid. If you continue, those edits will be discarded."
                confirmButtonTitle="Discard and continue"
                submitClick={confirmDiscardUnsavedNavigation}
                cancelClick={cancelDiscardUnsavedNavigation}
                size="max-w-lg"
                testId="product-catalog-discard-unsaved-navigation"
            />
        </>
    );
};

export default ProductCatalog;
